import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { staffMovementApi } from '../../api/staff-movement';
import { employeeApi } from '../../api/hr';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Send } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  promotion: 'Promotion', transfer: 'Transfer', position_change: 'Position Change', department_change: 'Department Change',
  duty_station_change: 'Duty Station Change', supervisor_change: 'Supervisor Change', grade_step_change: 'Grade/Step Change',
  salary_adjustment: 'Salary Adjustment', temporary_assignment: 'Temporary Assignment', other: 'Other',
};
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  approved: 'Approved', approved_pending_effective: 'Approved - Pending Effective Date', implemented: 'Implemented', rejected: 'Rejected', cancelled: 'Cancelled',
};

/** Staff can view their own movement history and request eligible movements (e.g. requesting a transfer) - they can never edit the Staff Master Record directly through this. */
export function MyStaffMovementPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const mineQuery = useQuery({ queryKey: ['my-staff-movements'], queryFn: () => staffMovementApi.mine() });
  const myProfileQuery = useQuery({ queryKey: ['my-profile'], queryFn: () => employeeApi.getMe() });

  const { register, handleSubmit, reset } = useForm<{ movementType: string; effectiveDate: string; reason: string; justification: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { movementType: string; effectiveDate: string; reason: string; justification: string }) => staffMovementApi.create({ employeeId: myProfileQuery.data!.data.id, ...v }),
    onSuccess: () => { showSuccess('Movement request submitted as a draft.'); queryClient.invalidateQueries({ queryKey: ['my-staff-movements'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create request.'),
  });
  const submitMutation = useMutation({ mutationFn: (id: number) => staffMovementApi.submit(id), onSuccess: () => { showSuccess('Submitted.'); queryClient.invalidateQueries({ queryKey: ['my-staff-movements'] }); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <PageHeader title="My Staff Movement" description="Request a transfer or other eligible movement - HR reviews and approves every change; nothing here edits your record directly." />

      <section className="card">
        <h2>Request a Movement</h2>
        <form className="inline-form" onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
          <label>
            Type
            <select {...register('movementType', { required: true })}>
              <option value="">- select -</option>
              {Object.entries(MOVEMENT_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label>Effective Date<input type="date" {...register('effectiveDate', { required: true })} /></label>
          <label>Reason<input {...register('reason')} /></label>
          <label>Justification<input {...register('justification', { required: true })} /></label>
          <button type="submit" disabled={createMutation.isPending || !myProfileQuery.data}>Create Draft</button>
        </form>
      </section>

      <div className="panel">
        <SectionHeader title="My Movement Requests" />
        <div className="panel-body">
        {mineQuery.data && (
          <DataTable
            rows={mineQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No movement requests yet."
            columns={[
              { header: 'Reference', render: (r) => r.movement_reference },
              { header: 'Type', render: (r) => MOVEMENT_TYPE_LABELS[r.movement_type] ?? r.movement_type },
              { header: 'Effective Date', render: (r) => r.effective_date },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => r.status === 'draft' && <IconActionButton icon={Send} label="Submit" onClick={() => submitMutation.mutate(r.id)} /> },
            ]}
          />
        )}
        {mineQuery.data?.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => staffMovementApi.resubmit(r.id)} queryKeysToInvalidate={[['my-staff-movements']]} />
        ))}
        </div>
      </div>
    </div>
  );
}
